const API_BASE = process.env.REACT_APP_API_URL || '';

// Helper to convert VAPID public key for push subscription
export const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const api = {
  _username: null,
  // Authenticate against the server, which verifies the password once and sets an
  // httpOnly JWT session cookie. We persist ONLY the username (for UI + "remember me"),
  // never the password. The cookie is sent automatically via credentials: 'include'.
  async login(username, password, rememberMe = false) {
    const user = await this.post('/api/login', { username, password, remember_me: rememberMe });
    this._username = username;
    localStorage.removeItem('auth');
    sessionStorage.removeItem('auth');
    (rememberMe ? localStorage : sessionStorage).setItem('auth', JSON.stringify({ username, rememberMe }));
    return user;
  },
  loadCredentials() {
    // Returns { username, rememberMe } for UI purposes. The actual session lives in the
    // httpOnly cookie, so a valid session is confirmed by calling /api/me.
    const saved = sessionStorage.getItem('auth') || localStorage.getItem('auth');
    if (saved) {
      const info = JSON.parse(saved);
      this._username = info.username;
      return info;
    }
    return null;
  },
  clearCredentials() {
    this._username = null;
    localStorage.removeItem('auth');
    sessionStorage.removeItem('auth');
    // Fire-and-forget: clear the server-side session cookie too.
    fetch(`${API_BASE}/api/logout`, { method: 'POST', credentials: 'include' }).catch(() => {});
  },
  async fetch(endpoint, options = {}) {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    const response = await fetch(`${API_BASE}${endpoint}`, { ...options, credentials: 'include', headers });
    if (response.status === 401) {
      // A 401 on a single endpoint must NOT tear down the whole session. Previously this
      // called clearCredentials() (which fires a server logout that deletes the cookie),
      // so one incidental 401 cascaded into logging the user out. Just surface the error;
      // genuine session expiry is handled by the /api/me check on app load.
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      const err = new Error(error.detail || 'Request failed');
      err.status = response.status;
      err.response = { status: response.status, data: error };
      throw err;
    }
    if (response.headers.get('content-type')?.includes('text/csv')) return response.blob();
    return response.json();
  },
  get(endpoint) { return this.fetch(endpoint); },
  post(endpoint, data) { return this.fetch(endpoint, { method: 'POST', body: JSON.stringify(data) }); },
  put(endpoint, data) { return this.fetch(endpoint, { method: 'PUT', body: JSON.stringify(data) }); },
  delete(endpoint) { return this.fetch(endpoint, { method: 'DELETE' }); }
};

export { API_BASE };
export default api;
